import { CompType } from '@aloft/toolkit-shared';
import { Flex, Typography, colors, theme } from '@aloft/toolkit-sui';

import { useCallback, useMemo, useState } from 'react';
import { CartesianGrid, ResponsiveContainer, Scatter, ScatterChart, XAxis, YAxis } from 'recharts';

import {
  ReconciliationSummarizedCostData,
  selectCompTypeReconciliationData,
} from '../../../redux/features/selectors/selectReconciliationData.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { ChartLayer } from './components/ChartLayer.tsx';
import { LegendItem } from './components/Legend.tsx';
import { Tooltip } from './components/Tooltip.tsx';
import {
  CHART_FONT_FAMILY,
  LEGEND_ITEM_SPACING,
  MARGIN,
  X_AXIS_HEIGHT,
  Y_AXIS_WIDTH,
} from './consts.ts';
import { RowData, TooltipState } from './types.ts';
import { computeChartDomain } from './utils/chartDomain.ts';
import { makeGridLineHorizontal, makeGridLineVertical } from './utils/gridLines.tsx';

export const ApproachesToValueChartUnwrapped = ({
  reconciliationData,
  height = 150,
  disableAnimations = false,
}: {
  reconciliationData: ReconciliationSummarizedCostData;
  height?: number;
  disableAnimations?: boolean;
}) => {
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [tooltip, setTooltip] = useState<TooltipState>(null);

  const handleResize = useCallback((width: number, height: number) => {
    setSize({ width, height });
  }, []);

  const chartData: RowData[] = useMemo(
    () => [
      {
        type: 'Cost Approach',
        value: reconciliationData.costApproachValue?.toDollarLike(),
      },
      {
        type: 'Sales Comparison',
        min: reconciliationData.minCompMarketAdjustedSalePrice?.toDollarLike(),
        max: reconciliationData.maxCompMarketAdjustedSalePrice?.toDollarLike(),
        median: reconciliationData.medianCompMarketAdjustedSalePrice?.toDollarLike(),
        average: reconciliationData.averageCompMarketAdjustedSalePrice?.toDollarLike(),
      },
    ],
    [reconciliationData],
  );

  const plotWidth = size.width - MARGIN.left - MARGIN.right - Y_AXIS_WIDTH;
  const plotHeight = size.height - MARGIN.top - MARGIN.bottom;

  const { minValue, maxValue, ticks } = computeChartDomain(chartData);

  const GridLineVertical = useMemo(() => makeGridLineVertical(ticks.length - 1), [ticks.length]);
  const GridLineHorizontal = useMemo(
    () => makeGridLineHorizontal(MARGIN.top, size.height - MARGIN.bottom - X_AXIS_HEIGHT),
    [size.height],
  );

  const plotOffsetX = MARGIN.left + Y_AXIS_WIDTH;
  const plotOffsetY = MARGIN.top;
  return (
    <ResponsiveContainer width="100%" height={height} onResize={handleResize}>
      <ScatterChart margin={MARGIN}>
        <XAxis
          type="number"
          dataKey="x"
          domain={[minValue, maxValue]}
          ticks={ticks}
          tickFormatter={(v: number) => `$${Math.round(v / 1000).toLocaleString('en-US')}k`}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.colors.graphNeutral, fontFamily: CHART_FONT_FAMILY }}
        />
        <YAxis
          type="category"
          dataKey="y"
          width={Y_AXIS_WIDTH}
          allowDuplicatedCategory={false}
          domain={chartData.map((d) => d.type)}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 14, fill: theme.colors.graphNeutral, fontFamily: CHART_FONT_FAMILY }}
        />
        <CartesianGrid vertical={GridLineVertical} horizontal={GridLineHorizontal} />
        {/* Dummy scatter — maps type→y so Recharts registers all categories on the Y axis */}
        <Scatter
          data={chartData.map((d) => ({ x: minValue, y: d.type }))}
          opacity={0}
          isAnimationActive={!disableAnimations}
        />
        {plotWidth > 0 && (
          <foreignObject x={plotOffsetX} y={plotOffsetY} width={plotWidth} height={plotHeight}>
            <svg width={plotWidth} height={plotHeight} overflow="visible">
              <ChartLayer
                plotWidth={plotWidth}
                plotHeight={plotHeight}
                onHover={setTooltip}
                chartData={chartData}
                minValue={minValue}
                maxValue={maxValue}
              />
            </svg>
          </foreignObject>
        )}
        {/* Legend rendered inside the chart SVG, top-right corner */}
        {size.width > 0 && (
          <g>
            <LegendItem
              color={theme.colors.graphAverage}
              label="Average"
              rightX={size.width - MARGIN.right - LEGEND_ITEM_SPACING}
              y={MARGIN.top / 2}
            />
            <LegendItem
              color={theme.colors.graphMedian}
              label="Median"
              rightX={size.width - MARGIN.right}
              y={MARGIN.top / 2}
            />
          </g>
        )}
        <Tooltip tooltip={tooltip} plotOffsetX={plotOffsetX} plotOffsetY={plotOffsetY} />
      </ScatterChart>
    </ResponsiveContainer>
  );
};

type ApproachesToValueChartProps = {
  compType?: CompType;
  disableAnimations?: boolean;
};

export const ApproachesToValueChart = ({
  compType,
  disableAnimations = false,
}: ApproachesToValueChartProps) => {
  const reconciliationData = useAppSelector((state) =>
    selectCompTypeReconciliationData(state, compType),
  );
  return (
    <Flex flexDirection="column" gap={4} width="100%">
      <Flex alignItems="center" gap={2}>
        <Typography variant="h3" color="textSecondary">
          Approaches to Value
        </Typography>
        {compType && (
          <Typography
            color={compType === 'arv' ? 'textOffset' : 'aivText'}
            __dangerDoNotUseSx={{
              backgroundColor: compType === 'arv' ? colors.arv : colors.aiv,
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            {compType === 'arv' ? 'ARV' : 'AIV'}
          </Typography>
        )}
      </Flex>

      <Flex
        width="100%"
        borderColor="strokeDefault"
        borderStyle="solid"
        borderWidth="1px"
        borderRadius="5px"
        paddingRight="15px"
        paddingTop="15px"
      >
        <ApproachesToValueChartUnwrapped
          reconciliationData={reconciliationData}
          disableAnimations={disableAnimations}
        />
      </Flex>
    </Flex>
  );
};
